import { Injectable } from '@angular/core';
import { AssignmentSectionQuery } from '@examdojo/assignment';
import { ChallengeActivityQuery } from '@examdojo/challenge';
import { DateTimeService } from '@examdojo/core/date-time';
import { shareOneReplay } from '@examdojo/core/rxjs';
import { isNotNullish } from '@examdojo/core/util/nullish';
import {
  distinctUntilChanged,
  distinctUntilKeyChanged,
  filter,
  map,
  Observable,
  of,
  switchMap,
  takeWhile,
  timer,
} from 'rxjs';

export interface ExamTimeState {
  remainingSeconds: number;
  timeLimitInMinutes: number;
}

@Injectable()
export class ExamTimeService {
  constructor(
    private readonly challengeActivityQuery: ChallengeActivityQuery,
    private readonly assignmentSectionQuery: AssignmentSectionQuery,
    private readonly dateTimeService: DateTimeService,
  ) {}

  readonly timerData$: Observable<ExamTimeState | null> = this.challengeActivityQuery.activeEntity$.pipe(
    filter(isNotNullish),
    filter((activity) => activity.status === 'IN_PROGRESS'),
    distinctUntilKeyChanged('id'),
    switchMap((activity) => {
      if (!activity) {
        return of(null);
      }

      const assignmentSection = this.assignmentSectionQuery.getEntity(activity.assignment_section_id);

      if (!assignmentSection) {
        return of(null);
      }

      const createdAt = activity.created_at;
      const timeLimitInMinutes = assignmentSection.time_limit_in_minutes;

      if (!timeLimitInMinutes || !createdAt) {
        return of(null);
      }

      const totalSeconds = timeLimitInMinutes * 60;

      return timer(0, 1000).pipe(
        map(() => {
          const now = new Date();
          const elapsedSeconds = Math.max(0, this.dateTimeService.diff(createdAt, now.toISOString(), 'seconds'));
          const remainingSeconds = Math.max(0, totalSeconds - elapsedSeconds);

          return {
            remainingSeconds,
            timeLimitInMinutes,
          };
        }),
        takeWhile((state) => state.remainingSeconds > 0, true),
      );
    }),
    shareOneReplay(),
  );

  readonly isTimeUp$ = this.timerData$.pipe(
    map((data) => (data ? data.remainingSeconds <= 0 : false)),
    distinctUntilChanged(),
  );
}
